import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import Breadcrumbs from '../components/Breadcrumbs';
import ColourSwatchGrid from '../components/ColourSwatchGrid';
import ColourDetailModal from '../components/ColourDetailModal';
import { SAULEDA_COLOURS, COLOUR_CATEGORIES, STOCK_COLOUR_CODE, TOTAL_COLOURWAYS } from '../data/sauledaColours';
import { COLOUR_LEAD_TIME_NOTE } from '../config/leadTimes';
import { colourChartAvailable } from '../prerenderData';
import './ColoursPage.css';

const CHART_PDF = '/docs/asg-sauleda-colour-chart.pdf';

export default function ColoursPage() {
  const [filter, setFilter] = useState('All');
  const [selected, setSelected] = useState(null);

  const filtered = useMemo(
    () => (filter === 'All' ? SAULEDA_COLOURS : SAULEDA_COLOURS.filter((c) => c.category === filter)),
    [filter]
  );

  // Only offer categories that actually have colourways in the data, so the
  // chips stay honest while the list is still being filled in.
  const chips = useMemo(() => {
    const present = new Set(SAULEDA_COLOURS.map((c) => c.category));
    return ['All', ...COLOUR_CATEGORIES.filter((c) => present.has(c))];
  }, []);

  return (
    <div className="colours-page">
      <SEO
        title="Awning Fabric Colours - Spanish Sauleda Range"
        description={`The full Spanish Sauleda awning fabric range - ${TOTAL_COLOURWAYS} colourways in solids and stripes. Charcoal grey held in stock; other colourways made to order.`}
        keywords="awning fabric colours, Sauleda fabric Melbourne, awning colour chart, retractable awning fabric"
        canonical="/colours"
        jsonLd={[SEO.breadcrumbSchema([{ name: 'Colours' }]), SEO.webPageSchema({ type: 'CollectionPage', name: 'Awning Fabric Colours', description: 'The full Spanish Sauleda awning fabric colour range.', url: '/colours' })]}
      />

      <section className="clr-hero">
        <div className="clr-hero-content">
          <p className="clr-label">Fabric Colours</p>
          <h1 className="clr-title">The full Sauleda range.</h1>
          <p className="clr-subtitle">
            Spanish Sauleda acrylic, woven and dyed in Catalonia. Every model in our awning
            range can be specified in any of these colourways.
          </p>
        </div>
      </section>

      <Breadcrumbs items={[{ name: 'Colours' }]} />

      <section className="clr-main">
        <div className="clr-inner">
          {/* Rendered only when the PDF has actually been added - see
              scripts/generate-prerender-data.mjs. */}
          {colourChartAvailable && (
            <a className="clr-download" href={CHART_PDF} target="_blank" rel="noopener noreferrer" download>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
                <path d="M12 3v11m0 0 4-4m-4 4-4-4M5 19h14" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              Download the full colour chart (PDF)
            </a>
          )}

          <p className="clr-leadtime">{COLOUR_LEAD_TIME_NOTE}</p>

          <div className="clr-filters" role="group" aria-label="Filter colours by type">
            {chips.map((c) => (
              <button
                key={c}
                type="button"
                className={`clr-chip${filter === c ? ' clr-chip--active' : ''}`}
                aria-pressed={filter === c}
                onClick={() => setFilter(c)}
              >
                {c === 'All' ? `All colours (${SAULEDA_COLOURS.length})` : `${c}s`}
              </button>
            ))}
          </div>

          <ColourSwatchGrid
            colours={filtered}
            onSelect={setSelected}
            stockCode={STOCK_COLOUR_CODE}
          />

          {SAULEDA_COLOURS.length < TOTAL_COLOURWAYS && (
            <p className="clr-note">
              Showing {SAULEDA_COLOURS.length} of {TOTAL_COLOURWAYS} colourways. The remainder
              are being added - ask us for any colour from the Sauleda range in the meantime.
            </p>
          )}

          <p className="clr-note">
            Colours shown are a guide only - screens vary. Ask us for the exact shade before
            you order.
          </p>

          <div className="clr-cta">
            <Link to="/request-quote" className="asg-btn-secondary">Request a quote</Link>
          </div>
        </div>
      </section>

      <ColourDetailModal
        colour={selected}
        onClose={() => setSelected(null)}
        isStock={selected ? selected.code === STOCK_COLOUR_CODE : false}
        stockNote="Held in stock - can often ship the next business day."
      />
    </div>
  );
}
